import { useQuery } from '@tanstack/react-query';

import { artiaProtectedApi } from '@/lib/api-client';
import { MarketShareData, MarketShareParams } from '../types/market-share';

const getMarketShareData = async (params: MarketShareParams): Promise<MarketShareData> => {
  // Safety check - don't make API call with invalid params
  if (!params.marketBasketId || params.marketBasketId <= 0) {
    return Promise.reject(new Error('Invalid marketBasketId'));
  }
  if (!params.stateCode) {
    return Promise.reject(new Error('Missing stateCode'));
  }
  if (params.utilizationTypeId === undefined || params.utilizationTypeId === null || params.utilizationTypeId < 0) {
    return Promise.reject(new Error('Invalid utilizationTypeId'));
  }

  const searchParams = new URLSearchParams({
    drugId: params.drugId.toString(),
    marketBasketId: params.marketBasketId.toString(),
    stateCode: params.stateCode,
    utilizationPeriod: params.utilizationPeriod,
    utilizationTypeId: params.utilizationTypeId.toString(),
  });

  const url = `market-share?${searchParams.toString()}`;

  try {
    // artiaProtectedApi returns data directly due to response interceptor
    const response: any = await artiaProtectedApi.get(url);

    // Handle the typo in the API response - "quaters" should be "quarters"
    if (response && response.quaters) {
      const correctedData: MarketShareData = {
        ...response,
        quarters: response.quaters
      };
      delete (correctedData as any).quaters;
      return correctedData;
    }

    return response as MarketShareData;
  } catch (error) {
    throw error;
  };
};

export const makeMarketShareDataQueryKey = (params: MarketShareParams) => [
  'market-share',
  'data',
  params.drugId,
  params.marketBasketId,
  params.stateCode,
  params.utilizationPeriod,
  params.utilizationTypeId,
];

export const useMarketShareData = (params: MarketShareParams, enabled = true) => {
  // Add safety check to prevent invalid API calls
  const isValidParams = params.marketBasketId > 0 && !!params.stateCode && params.utilizationTypeId >= 0;

  return useQuery({
    queryKey: makeMarketShareDataQueryKey(params),
    queryFn: () => getMarketShareData(params),
    enabled: enabled && isValidParams,
    retry: false, // Disable retry to prevent infinite loops
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
